import { useState, useEffect } from 'react';

/**
 * Reuse the Google Maps API already loaded app-wide by PrivateLayout via
 * loadScript() below. Loading it a second time — e.g. via GoogleApiWrapper —
 * triggers Google's "included the Google Maps JavaScript API multiple times"
 * error and breaks the map.
 *
 * Resolves immediately when the API is already there; otherwise waits on the
 * 'google-script-loaded' event dispatched by loadScript (with a polling
 * fallback, in case this mounts mid-load). Gives up after `timeoutMs` and
 * reports `failed` so callers can show an error instead of spinning forever
 * when the script is blocked/offline/quota'd.
 *
 * @returns {{ google: object|null, failed: boolean }}
 */
export const useGoogle = ({ timeoutMs = 15000 } = {}) => {
  const [google, setGoogle] = useState(() =>
    window.google && window.google.maps ? window.google : null
  );
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (google) return undefined;
    let poll;
    let timer;
    const stop = () => {
      clearInterval(poll);
      clearTimeout(timer);
      document.removeEventListener('google-script-loaded', check);
    };
    function check() {
      if (window.google && window.google.maps) {
        setGoogle(window.google);
        stop();
        return true;
      }
      return false;
    }
    document.addEventListener('google-script-loaded', check);
    poll = setInterval(check, 300);
    timer = setTimeout(() => {
      if (!check()) {
        setFailed(true);
        stop();
      }
    }, timeoutMs);
    return stop;
  }, [google, timeoutMs]);

  return { google, failed };
};

/**
 * The map centre for a property, or null when it has no usable coordinates.
 * Property records expose lat/lng (ST_X/ST_Y of properties.location).
 */
export const propertyCenter = (property) => {
  const lat = parseFloat(property?.lat);
  const lng = parseFloat(property?.lng);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

export const loadScript = (url) => {
  let script = document.createElement("script");
  script.id = "google-api-script";
  script.type = "text/javascript";

  const createEvent = () => {
    var event = new CustomEvent('google-script-loaded');
    document.dispatchEvent(event);
  }

  if (script.readyState) {
    script.onreadystatechange = function() {
      if (script.readyState === "loaded" || script.readyState === "complete") {
        script.onreadystatechange = null;
        createEvent();
      }
    };
  } else {
    script.onload = createEvent;
  }

  script.src = url;
  if(!document.getElementById('google-api-script')){
    document.getElementsByTagName("head")[0].appendChild(script);
  }
};
